import React from 'react';
import Link from 'next/link';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Icon from '@ui/Icon';
import { BreadcrumbItem } from '.';
import { alphaWhite } from '../../../../util/cssUtil';

const useStyles = makeStyles<{ disableLink: boolean }>()(
  (theme, { disableLink }) => ({
    breadcrumb: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      height: '100%',
      maxWidth: '200px',
    },
    label: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: palette.gray_75,
      whiteSpace: 'nowrap',
      '&:hover': {
        color: disableLink ? undefined : palette.gray_0,
      },
    },
    link: {
      textDecoration: 'none !important',
      borderRadius: theme.spacing(0.5),
      padding: theme.spacing(0.75, 1),
      '&:hover': {
        backgroundColor: disableLink ? undefined : alphaWhite(0.09),
      },
      transition: theme.transitions.create('background-color', {
        duration: 250,
        easing: theme.transitions.easing.easeInOut,
      }),
    },
  })
);

interface BreadcrumbProps {
  route: BreadcrumbItem;
  className?: string;
  /**
   * By default, all breadcrumb items are not clickable, unless explicity set to false
   */
  disableLink?: boolean;
  /**
   * Marks this crumb as the page the user is on. Kept separate from
   * `disableLink`: a crumb can be a real link without being the current page,
   * and the last crumb is usually the current page while not being a link.
   *
   * No caller sets this yet, so `aria-current` currently renders nowhere —
   * which matches what shipped before, since the marker used to be inferred
   * from `disableLink` and no crumb ever set that either. The trail whose last
   * crumb is genuinely the current page renders that crumb through
   * `FoldersNavBreadcrumb`, which has no per-crumb hook to hang this on; giving
   * it one is deliberately out of scope for now.
   */
  isCurrentPage?: boolean;
}

export default function Breadcrumb({
  route,
  className,
  disableLink = true,
  isCurrentPage = false,
}: BreadcrumbProps) {
  const { classes, cx } = useStyles({ disableLink });

  const href = !disableLink && route.route;
  const ariaCurrent = isCurrentPage ? 'page' : undefined;

  return (
    <Box
      component="li"
      fontSize={14}
      className={cx(classes.breadcrumb, className)}
    >
      {href ? (
        <Link
          href={href}
          passHref={!!href}
          aria-current={ariaCurrent}
          className={cx(classes.link, classes.label)}
        >
          {route.iconType && (
            <Icon type={route.iconType} style={{ color: palette.gray_75 }} />
          )}
          {route.label}
        </Link>
      ) : (
        <div className={classes.label} aria-current={ariaCurrent}>
          {route.iconType && (
            <Icon type={route.iconType} style={{ color: palette.gray_75 }} />
          )}
          {route.label}
        </div>
      )}
    </Box>
  );
}
